/**
 * Turning a message body into something readable, without handing the sender
 * the page.
 *
 * Mail markup is hostile input. It arrives from anyone, it is never seen by a
 * server that could scrub it, and the usual answer — sanitise and render — is
 * an arms race against every parser quirk in the browser. So this does the
 * opposite: it parses the markup into an inert document, walks it, and builds a
 * **new** tree out of an allowlist of elements this file constructs itself.
 * Nothing from the sender is ever passed to `innerHTML`, and an element that is
 * not on the list contributes its words and nothing else.
 *
 * Two things the reader actually wanted, and their limits:
 *
 * - **Links** are rendered as links, `http(s)` and `mailto:` only, and always
 *   with `rel="noopener noreferrer"`. `javascript:` and `data:` hrefs are
 *   dropped to plain text. A bare URL, a bare address and a markdown
 *   `[Thing](https://…)` in running text are all made clickable.
 * - **Images** are rendered when the message carries them: a `cid:` reference
 *   resolved from the message's own parts, or an inline `data:` image. A
 *   **remote** image is never fetched — it is the classic read receipt, and
 *   this client blocks trackers — so it renders as a small placeholder saying
 *   so.
 */

/** Elements worth keeping, mapped to the tag this file will create. */
const BLOCK: Record<string, string> = {
  P: "p", DIV: "div", BR: "br", HR: "hr",
  H1: "h3", H2: "h3", H3: "h3", H4: "h4", H5: "h5", H6: "h6",
  UL: "ul", OL: "ol", LI: "li",
  BLOCKQUOTE: "blockquote", PRE: "pre",
  TABLE: "table", THEAD: "tbody", TBODY: "tbody", TR: "tr", TD: "td", TH: "th",
};

const INLINE: Record<string, string> = {
  B: "strong", STRONG: "strong",
  I: "em", EM: "em",
  U: "u", CODE: "code", SPAN: "span", SMALL: "small", SUB: "sub", SUP: "sup",
};

/** Dropped whole, contents and all: these carry no words a reader wants. */
const MUTE = new Set(["SCRIPT", "STYLE", "HEAD", "TITLE", "META", "LINK", "IFRAME", "OBJECT", "EMBED", "NOSCRIPT", "SVG", "FORM", "INPUT", "BUTTON"]);

export interface ImageSource {
  /** Resolve a `cid:` reference to a displayable URL, or null if not yet held. */
  resolve(contentID: string): string | null;
  /**
   * Whether the message carries this part at all.
   *
   * The difference matters to the reader: a part that exists but is not fetched
   * is one click away, while a `cid:` with no part behind it is a reference to
   * something the message does not contain and never will. Saying "not loaded"
   * for the second is a promise the client cannot keep.
   */
  has(contentID: string): boolean;
  /**
   * A remote image the page fetched for this reader, by its absolute address.
   * Null for one that was not fetched, so it renders blocked — the CSP would
   * refuse a remote `src` anyway, and a tracker is a tracker until this
   * account says otherwise.
   */
  remote?(address: string): string | null;
}

/** Render a plain-text body: paragraphs preserved, bare URLs made clickable. */
export function renderText(text: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  fragment.append(...linkify(text));
  return fragment;
}

/** Render an HTML body into a tree built entirely from the allowlist above. */
export function renderHtml(html: string, images?: ImageSource): DocumentFragment {
  // An inert document: it runs nothing and fetches nothing, so the sender's
  // markup is parsed without ever being live.
  const doc = new DOMParser().parseFromString(html, "text/html");
  const fragment = document.createDocumentFragment();
  for (const child of Array.from(doc.body?.childNodes ?? [])) {
    convert(child, fragment, images);
  }
  return fragment;
}

type Parent = HTMLElement | DocumentFragment;

function convert(node: Node, into: Parent, images?: ImageSource): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.nodeValue ?? "";
    if (text.trim() || text.includes(" ")) into.append(...linkify(text));
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;

  const element = node as Element;
  if (MUTE.has(element.tagName)) return;

  if (element.tagName === "A") {
    into.append(anchor(element, images));
    return;
  }
  if (element.tagName === "IMG") {
    into.append(image(element, images));
    return;
  }

  const tag = BLOCK[element.tagName] ?? INLINE[element.tagName];
  // Not on the list: keep the words, drop the element. A message must never
  // lose text because this file has not heard of a tag.
  const target: Parent = tag ? document.createElement(tag) : into;
  if (tag) applyFontSize(element, target as HTMLElement);
  for (const child of Array.from(element.childNodes)) convert(child, target, images);
  if (tag) into.append(target);
}

/**
 * The size a sender asked for, and nothing else they asked for.
 *
 * A signature is laid out at a size: the logo is 201px wide in a 7530px image,
 * and the small print under it is 10px. Dropping every attribute rendered both
 * at the column's full width, which is the sender's message reproduced
 * incorrectly. So two properties survive — a font size and an image's
 * dimensions — read as integers and re-emitted as numbers this file wrote. No
 * colour, no positioning, no `url()`: the rest of a sender's CSS can hide text,
 * cover the page or fetch something, and none of it is worth a signature.
 */
const MIN_FONT = 9;
const MAX_FONT = 36;

function applyFontSize(from: Element, to: HTMLElement): void {
  const size = pixels(styleValue(from, "font-size"));
  if (size) to.style.fontSize = `${Math.min(Math.max(size, MIN_FONT), MAX_FONT)}px`;
}

/** A whole number of CSS pixels, bare or with `px`. Anything else is not a size. */
function pixels(value: string | null): number | null {
  const match = /^(\d{1,4})(?:\.\d+)?(?:px)?$/i.exec((value ?? "").trim());
  const size = match ? Number(match[1]) : 0;
  return size > 0 ? size : null;
}

function styleValue(element: Element, property: string): string | null {
  const style = element.getAttribute("style");
  if (!style) return null;
  const match = new RegExp(`(?:^|;)\\s*${property}\\s*:([^;]+)`, "i").exec(style);
  return match?.[1]?.trim() ?? null;
}

function anchor(element: Element, images?: ImageSource): HTMLElement {
  const href = safeHref(element.getAttribute("href"));
  const link: HTMLElement = document.createElement(href ? "a" : "span");
  if (href && link instanceof HTMLAnchorElement) {
    link.href = href;
    link.target = "_blank";
    // noopener is the one that matters: without it the opened page can navigate
    // this one. nofollow because a mail body is not an endorsement.
    link.rel = "noopener noreferrer nofollow";
    link.title = href;
  }
  applyFontSize(element, link);
  for (const child of Array.from(element.childNodes)) convert(child, link, images);
  if (!link.textContent?.trim() && href) link.textContent = href;
  return link;
}

function image(element: Element, images?: ImageSource): HTMLElement {
  const src = element.getAttribute("src")?.trim() ?? "";
  const alt = element.getAttribute("alt")?.trim() ?? "";

  const embedded = src.toLowerCase().startsWith("cid:");
  const address = embedded ? null : remoteOf(src);
  const resolved = embedded
    ? images?.resolve(cidOf(src)) ?? null
    : src.toLowerCase().startsWith("data:image/")
      ? src
      : address
        ? images?.remote?.(address) ?? null
        : null;

  if (!resolved) {
    // Three different absences, and saying which is which matters. One is a
    // tracker this client refuses on principle — loading it would tell the
    // sender the message was opened, when, and from where. One is a part the
    // message carries, one click away. The third is a reference to something
    // the message does not contain, which no amount of clicking will produce.
    const carried = embedded && (images?.has(cidOf(src)) ?? false);
    const placeholder = document.createElement("span");
    placeholder.className = !embedded ? "blocked-image" : carried ? "pending-image" : "missing-image";
    const label = !embedded
      ? "Remote image blocked"
      : carried
        ? "Image not loaded"
        : "Image missing from this message";
    placeholder.textContent = alt ? `${label} — ${alt}` : label;
    return placeholder;
  }

  const img = document.createElement("img");
  img.src = resolved;
  img.alt = alt;
  img.loading = "lazy";
  img.decoding = "async";
  // The stylesheet caps the width and takes the height from the aspect ratio,
  // so a declared size scales the image down and never past the column.
  const width = pixels(element.getAttribute("width")) ?? pixels(styleValue(element, "width"));
  const height = pixels(element.getAttribute("height")) ?? pixels(styleValue(element, "height"));
  if (width) img.width = width;
  if (height) img.height = height;
  return img;
}

/** The bare content id: `cid:<logo@example>` and `cid:logo@example` are the same part. */
function cidOf(src: string): string {
  return src.slice(4).replace(/^<|>$/g, "").trim();
}

/** The absolute address of a remote image, or null. Relative and `data:` are not remote. */
function remoteOf(src: string): string | null {
  if (!src || src.toLowerCase().startsWith("cid:") || src.toLowerCase().startsWith("data:")) {
    return null;
  }
  try {
    const url = new URL(src, "https://resolve.invalid/");
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    // Resolved against the placeholder base: a path with no host to resolve it
    // against fetches nothing, because there is nowhere to fetch it from.
    if (url.hostname === "resolve.invalid") return null;
    return url.href;
  } catch {
    return null;
  }
}

/**
 * Every remote image address in a body, once each, in order.
 *
 * The reader asks for these, and only these: a body with none renders without
 * the controls, and a sender's permission costs one run rather than a parse
 * per image.
 */
export function remoteImages(html: string): string[] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const seen = new Set<string>();
  for (const img of Array.from(doc.querySelectorAll("img"))) {
    const address = remoteOf(img.getAttribute("src")?.trim() ?? "");
    if (address) seen.add(address);
  }
  return [...seen];
}

/** `http(s)` and `mailto:` only. Everything else is not a link. */
export function safeHref(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  try {
    const url = new URL(trimmed, "https://invalid.example/");
    if (url.protocol === "http:" || url.protocol === "https:" || url.protocol === "mailto:") {
      // A relative href resolves against the placeholder base, which means the
      // sender wrote a path with nothing to resolve it against: not a link.
      if (url.hostname === "invalid.example" && !trimmed.toLowerCase().startsWith("http")) return null;
      return url.href;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Markdown link, bare URL, bare address — in that order.
 *
 * Markdown is first in the alternation because the regex engine takes the
 * leftmost branch that matches, and a `[Thing](https://…)` whose URL branch won
 * would render the address twice and the label not at all.
 */
const LINK_PATTERN = new RegExp([
  /\[([^\][\n]+)\]\(\s*<?([^\s<>()]+)>?\s*\)/,
  /(https?:\/\/[^\s<>()[\]{}"']+[^\s<>()[\]{}"'.,;:!?])/,
  /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/,
].map((part) => part.source).join("|"), "gi");

/**
 * Links in plain text, made clickable.
 *
 * A plain-text body has no markup to carry a link, so the reader is left
 * copying and pasting. Three shapes are recognised: `[Thing](https://…)`, which
 * senders and agents write constantly and which reads as punctuation
 * otherwise; a bare URL; and a bare address. The pattern deliberately stops
 * before trailing punctuation: `see https://example.com.` links the address,
 * not the sentence.
 */
export function linkify(text: string): Node[] {
  const out: Node[] = [];
  let last = 0;
  for (const match of text.matchAll(LINK_PATTERN)) {
    const at = match.index ?? 0;
    const [raw, label, target, url, address] = match;
    if (at > last) out.push(document.createTextNode(text.slice(last, at)));
    last = at + raw.length;
    const href = safeHref(target ?? url ?? `mailto:${address}`);
    // A label the sender chose can say anything, so a refused href keeps the
    // words exactly as written rather than quietly dropping the destination.
    out.push(href ? anchorTo(href, label ?? raw) : document.createTextNode(raw));
  }
  if (last < text.length) out.push(document.createTextNode(text.slice(last)));
  return out;
}

function anchorTo(href: string, label: string): HTMLAnchorElement {
  const link = document.createElement("a");
  link.href = href;
  link.textContent = label;
  link.target = "_blank";
  link.rel = "noopener noreferrer nofollow";
  if (label !== href) link.title = href;
  return link;
}
